import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonSize} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, BLUE_POWDER, GRAY_LIGHTER} from 'ui-library/Colors';
import {GRAY} from 'ui-library/Colors';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {JSONValue} from 'util/Types';

import {
  getConfigShapeAtConfigShapePath,
  maybeGetValueFromConfig,
} from '../step-config-settings/StepConfigSettingsUtils';
import {
  FIELD_ORDER_UI_SCHEMA_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  SHOW_DIVIDER_LINE_KEY,
  ShapeUISchemaV2,
  FieldUISchemaWithStandardComponentV2,
  isFieldUISchemaWithStandardComponentV2,
  CustomComponentEditorProps,
} from '../StepConfigEditorTypes';
import ColumnReferenceEditor from './ColumnReferenceEditor';
import StepConfigEditorRouterV2 from './StepConfigEditorRouterV2';
import StepConfigFieldsTemplate, {
  SectionNameOrNull,
  StepConfigField,
  VariableHeaderOrNull,
} from './StepConfigFieldsTemplate';

const isCollapsedGlobalState = makeUseGlobalClientState<boolean>(false);

// TODO(BNCH-120660): Make the uiSchema variable so we can use this component for the y axis section
// TODO(BNCH-120660): Move uiSchema to a new file
const X_AXIS_SECTION_UI_SCHEMA: ShapeUISchemaV2 = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [
    'xAxisColumnName',
    'xSortDirection',
    'xAxisMin',
    'xAxisMax',
    'xAxisLabel',
    'pointLabelColumnName',
  ],
  [SHOW_DIVIDER_LINE_KEY]: false,
  [SECTION_NAME_UI_SCHEMA_KEY]: 'X-axis',
  xAxisColumnName: {
    displayName: 'Column',
    configShapePath: ['chartConfig', 'parameters', 'xAxisColumnName'],
  },
  xAxisLabel: {
    displayName: 'Custom axis label',
    configShapePath: ['chartConfig', 'parameters', 'xAxisLabel'],
  },
  xSortDirection: {
    displayName: 'Sort',
    configShapePath: ['chartConfig', 'parameters', 'sortDirection'],
    fieldSettings: {
      displayNames: {
        ASCENDING: 'Ascending',
        DESCENDING: 'Descending',
      },
    },
  },
  // TODO(BNCH-120660): Make xAxisMin and xAxisMax fit in the same row
  xAxisMin: {
    displayName: 'Range min',
    configShapePath: ['chartConfig', 'parameters', 'xAxisMin'],
  },
  xAxisMax: {
    displayName: 'Range max',
    configShapePath: ['chartConfig', 'parameters', 'xAxisMax'],
  },
  pointLabelColumnName: {
    displayName: 'Label points by',
    configShapePath: ['chartConfig', 'parameters', 'pointLabelColumnName'],
  },
};

/**
 * @package
 *
 * We need a custom component here because the icon needs to grow vertically if the column name takes up
 * multiple lines.
 */
export function IconButton({
  isCollapsed,
  setIsCollapsed,
}: {
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
}): JSX.Element {
  return (
    <div
      className={css(
        styles.icon,
        isCollapsed && styles.iconBorderRadiusCollapsed,
        !isCollapsed && styles.iconBorderRadiusNotCollapsed
      )}
      onClick={() => {
        setIsCollapsed(!isCollapsed);
      }}
    >
      <BenchlingIcon icon="filter" />
    </div>
  );
}

/**
 *
 * Create a column editor that sits above the collapsible section.
 *
 * We are not using StepConfigEditorRouterV2 here because of some
 * styling needs (rounded corners, no bottom margin). This component
 * is outside of the StepConfigFieldsTemplate because we want the
 * column and the button to be the same width as the collapsing section.
 *
 * @package
 */
export function ColumnEditor({
  config,
  onChangeConfig,
  disabled,
  configShape,
  setIsCollapsed,
  isCollapsed,
  columnReferenceUiSchema,
}: {
  config: Record<string, JSONValue>;
  onChangeConfig: (config: JSONValue, path: Array<string>, newValue: JSONValue) => void;
  disabled?: boolean;
  configShape: PipelineConfigJsonSpec;
  setIsCollapsed: (isCollapsed: boolean) => void;
  isCollapsed: boolean;
  columnReferenceUiSchema: FieldUISchemaWithStandardComponentV2;
}): JSX.Element {
  assert(
    isFieldUISchemaWithStandardComponentV2(columnReferenceUiSchema),
    'First field must be a custom component'
  );
  const currentConfigShape = getConfigShapeAtConfigShapePath(
    columnReferenceUiSchema.configShapePath,
    configShape,
    config
  );
  assert(currentConfigShape.kind === 'columnReference', 'First field must be a column reference');

  return (
    <div className={css(styles.firstRowWrapper)}>
      <div className={css(styles.firstRow)}>
        <div className={css(styles.grow)}>
          <ColumnReferenceEditor
            config={maybeGetValueFromConfig(config, columnReferenceUiSchema.configShapePath) as string | null}
            configShape={currentConfigShape}
            onChangeConfig={(newValue) => {
              onChangeConfig(config, columnReferenceUiSchema.configShapePath, newValue);
            }}
            disabled={disabled}
            buttonSize={ButtonSize.SMALL}
            cornerRadius={
              isCollapsed
                ? {topLeft: 4, bottomLeft: 4, topRight: 0, bottomRight: 0}
                : {topLeft: 4, bottomLeft: 0, topRight: 0, bottomRight: 0}
            }
          />
        </div>

        <IconButton isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      </div>
    </div>
  );
}

/** @package */
export function CollapsingSection({
  collapsibleFields,
}: {
  collapsibleFields: Array<StepConfigField>;
}): JSX.Element {
  return (
    <div className={css(styles.collapsedSection)}>
      <StepConfigFieldsTemplate numColumns={1} fields={collapsibleFields} isCompact={true} />
    </div>
  );
}

export default function CollapsibleColumnSectionEditor({
  config,
  onChangeConfig,
  disabled,
  configShape,
}: CustomComponentEditorProps): JSX.Element {
  // TODO(BNCH-120660): Add analysis id to StepConfigEditorContext and use it as a global state client id
  // Otherwise when you switch tabs in the sidebar the state resets
  const [isCollapsed, setIsCollapsed] = isCollapsedGlobalState.useGlobalClientState(
    'COLLAPSIBLE_COLUMN_SECTION_EDITOR'
  );
  const uiSchema = X_AXIS_SECTION_UI_SCHEMA;
  const fieldOrder = uiSchema[FIELD_ORDER_UI_SCHEMA_KEY];
  const firstFieldUiSchema = uiSchema[fieldOrder[0]];
  assert(
    isFieldUISchemaWithStandardComponentV2(firstFieldUiSchema),
    'First field must be a standard component'
  );

  const sectionName = uiSchema[SECTION_NAME_UI_SCHEMA_KEY];
  const collapsibleFields = fieldOrder.slice(1).map((variableName) => {
    const field = uiSchema[variableName];
    assert(field != null, `Field ${variableName} is not defined in uiSchema`);
    assert(isFieldUISchemaWithStandardComponentV2(field), 'Field must be a standard component');
    return {
      variableName: field.displayName,
      infoText: field.infoText,
      editor: (
        <StepConfigEditorRouterV2
          key={variableName}
          config={config}
          configShape={configShape}
          dataTestAttrs={{key: variableName}}
          onChangeConfig={onChangeConfig}
          disabled={disabled}
          uiSchema={uiSchema[variableName]}
        />
      ),
    };
  });

  return (
    <Row>
      <SectionNameOrNull sectionName={sectionName} isCompact={true} />
      <Col sm={12}>
        <VariableHeaderOrNull
          variableName={firstFieldUiSchema?.displayName}
          infoText={firstFieldUiSchema?.infoText}
          isCompact={true}
        />
      </Col>
      <Col sm={12}>
        <ColumnEditor
          config={config}
          onChangeConfig={onChangeConfig}
          disabled={disabled}
          configShape={configShape}
          setIsCollapsed={setIsCollapsed}
          isCollapsed={isCollapsed}
          columnReferenceUiSchema={firstFieldUiSchema}
        />
      </Col>
      {!isCollapsed && (
        <Col sm={12}>
          <CollapsingSection collapsibleFields={collapsibleFields} />
        </Col>
      )}
    </Row>
  );
}

const styles = StyleSheet.create({
  collapsedSection: {
    borderWidth: '0 1px 1px 1px',
    borderStyle: 'solid',
    borderColor: GRAY,
    backgroundColor: GRAY_LIGHTER,
    borderRadius: '0 0 4px  4px',
    marginBottom: 10,
    paddingTop: 4,
  },
  firstRow: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
  },
  firstRowWrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  grow: {
    flexGrow: 1,
  },
  icon: {
    width: 28,
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: '1px 1px 1px 0',
    borderStyle: 'solid',
    borderColor: GRAY,
    color: BLUE_LIGHT,
    backgroundColor: BLUE_POWDER,
  },
  iconBorderRadiusNotCollapsed: {
    borderRadius: '0 4px 0 0',
  },
  iconBorderRadiusCollapsed: {
    borderRadius: '0 4px 4px 0',
  },
});
